// ProfilePage.tsx
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { Link } from 'react-router-dom';
import { SECURITY_FALLBACKS } from '../../../../../corelogic/domain/constants/displayFallbacks.constants';
import { CODE_TO_COUNTRY } from '../../../../../corelogic/domain/utils/country.utils';
import { SafeText } from '../../../../../corelogic/domain/utils/xssProtection.utils';
import { useFighterState } from '../../hooks/useFighterState';
import { useFightState } from '../../hooks/useFightState';
import { useUserProfile } from '../../hooks/useUserProfile';
import LoadingBar from '../Shared/LoadingBar';
import FavoriteFighterPicker from './components/FavoriteFighterPicker';
import ProfileStats from './components/ProfileStats';

// Style objects
const styles = {
  container: {
    height: '100%',
    width: '100%',
    display: 'flex',
    padding: '10px',
    flexDirection: 'column',
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)), url('/viewer_octagon.png')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    overflow: { xs: 'visible', lg: 'hidden' },
    minHeight: { xs: '100vh', lg: '100%' },
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    overflow: { xs: 'visible', lg: 'auto' },
    paddingLeft: '10px',
    transition: 'all 0.3s ease-in-out',
  },
  profileBox: {
    p: { xs: 2, md: 3 },
    mb: 3,
    borderRadius: 2,
    bgcolor: 'rgba(20, 20, 20, 0.9)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 2, sm: 3 },
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    height: { xs: 'auto', sm: '100px', md: '110px', lg: '120px' },
    width: '96%',
    marginLeft: '1.3%',
    transition: 'height 0.3s, padding 0.3s',
  },
  avatar: {
    width: { xs: 50, sm: 60, md: 70, lg: 80 },
    height: { xs: 50, sm: 60, md: 70, lg: 80 },
    border: '2px solid #cc0000',
  },
  username: {
    fontFamily: 'Rubik Mono One',
    color: '#ffffff',
    fontSize: 'clamp(1rem, 1.5vw, 1.3rem)',
  },
  country: {
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '2px',
    overflow: 'hidden',
    height: '30px',
    width: '45px',
    marginTop: '10px',
  },
  settingsButton: {
    color: '#ffffff',
    borderColor: '#cc0000',
    padding: '4px 8px',
    fontSize: { xs: '0.7rem', sm: 'clamp(0.8rem, 1.5vw, 1rem)' },
    '&:hover': {
      borderColor: '#ff0000',
      backgroundColor: 'rgba(204, 0, 0, 0.1)',
    },
  },
  statsContainer: {
    flexGrow: 1,
    overflow: { xs: 'visible', lg: 'auto' },
    display: 'flex',
    gap: 0,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: { xs: 1, sm: 2 },
    width: '100%',
    height: 'auto',
    minHeight: { xs: '300px', sm: '400px', md: '425px', lg: '450px' },
    maxHeight: { xs: 'none', sm: 'none', lg: '700px' },
    marginTop: 0,
    paddingBottom: 0,
    marginBottom: 0,
  },
  rightColumn: {
    paddingRight: '10px',
    overflow: { xs: 'visible', lg: 'auto' },
  },
};

const ProfilePage = () => {
  const { user, isLoading: isUserLoading } = useUserProfile();
  const { fighters: allFighters, isLoading: isFightersLoading } =
    useFighterState();
  const { fights: allFights, isLoading: isFightsLoading } = useFightState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // When all data is loaded, set loading to false
    if (!isFightersLoading && !isFightsLoading && !isUserLoading) {
      const timer = setTimeout(() => setLoading(false), 500);
      return () => clearTimeout(timer);
    } else {
      setLoading(true);
    }
  }, [isFightersLoading, isFightsLoading, isUserLoading]);

  // Framer Motion Container
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  // Framer Motion Item
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  return (
    <Container maxWidth={false} disableGutters sx={styles.container}>
      {/* Loading Bar */}
      {loading && <LoadingBar isLoading={true} />}

      <Grid
        container
        spacing={2}
        component={motion.div}
        variants={container}
        initial="hidden"
        animate="show"
        alignItems="stretch"
      >
        {/* Left Side */}
        <Grid item xs={12} lg={7} sx={styles.leftColumn}>
          {/* Top Section - User Profile Info */}
          <Box sx={styles.profileBox} component={motion.div} variants={item}>
            <Avatar
              src={user?.profilePicture || ''}
              alt={user?.username || 'Username'}
              sx={styles.avatar}
            />

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" sx={styles.username}>
                <SafeText
                  text={user?.username}
                  fallback={SECURITY_FALLBACKS.USERNAME}
                />
              </Typography>

              {user?.nationality && CODE_TO_COUNTRY[user.nationality] && (
                <Box sx={styles.country}>
                  <ReactCountryFlag
                    countryCode={user.nationality}
                    svg
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                      objectFit: 'cover',
                    }}
                    title={CODE_TO_COUNTRY[user.nationality]}
                  />
                </Box>
              )}
            </Box>

            <Button
              component={Link}
              to="/settings"
              variant="outlined"
              startIcon={<SettingsIcon />}
              sx={styles.settingsButton}
            >
              Settings
            </Button>
          </Box>

          {/* Bottom Section - Stats Cards */}
          <Box
            sx={styles.statsContainer}
            component={motion.div}
            variants={item}
          >
            {user && <ProfileStats user={user} />}
          </Box>
        </Grid>

        {/* Right Side - Favorite Fighter Picker */}
        <Grid item xs={12} lg={5} sx={styles.rightColumn}>
          <FavoriteFighterPicker fighters={allFighters} fights={allFights} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProfilePage;
